import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MoreVertical, Star } from 'lucide-react';
import { toast } from 'sonner';
import { shopAPI } from '../lib/api';
import { isLoggedIn } from '../lib/auth';
import { t } from '../lib/i18n';

export function Price({ course }) {
  if (course.is_free) return <span className="font-bold text-lms-free">{t('Free')}</span>;
  const discounted = !!course.discount_flag;
  return (
    <span className="lms-num">
      {discounted && (
        <span className="mr-1.5 text-[0.8em] font-normal text-lms-faint line-through">${Number(course.price).toFixed(2)}</span>
      )}
      <span className="font-bold text-lms-ink">
        ${Number(discounted ? course.discounted_price : course.price).toFixed(2)}
      </span>
    </span>
  );
}

export function Rating({ rating }) {
  if (!rating || !rating.count) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-lms-sub">
      <span className="font-bold text-lms-rating lms-num">{rating.avg}</span>
      <Star size={13} className="text-lms-rating" fill="currentColor" />
      <span className="lms-num">({rating.count})</span>
    </span>
  );
}

// Discount percentage badge, when a discount is active and computable.
function discountPct(course) {
  if (!course.discount_flag || !course.price) return null;
  const pct = Math.round((1 - Number(course.discounted_price) / Number(course.price)) * 100);
  return pct > 0 ? pct : null;
}

// Card ⋮ menu — one action today (wishlist). Lives OUTSIDE the card link so a
// click never navigates; closes on outside click / Escape.
function CardMenu({ courseId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleWishlist = async () => {
    setOpen(false);
    try {
      const { data } = await shopAPI.wishlistToggle(courseId);
      toast.success(data.wishlisted ? t('Added to your wishlist') : t('Removed from your wishlist'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  return (
    <div ref={ref} className="absolute right-[26px] top-[26px] z-10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('Course options')}
        aria-expanded={open}
        className="lms-press flex h-8 w-8 items-center justify-center rounded-lg bg-lms-chip-bg text-lms-chip-ink shadow-[var(--lms-elev-1)]"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-48 overflow-hidden rounded-lg border border-lms-line bg-lms-surface py-1 shadow-[var(--lms-elev-3)]">
          <button type="button" onClick={toggleWishlist} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-lms-ink hover:bg-lms-raised">
            <Heart size={15} /> {t('Add to / remove from wishlist')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function CourseCard({ course }) {
  const pct = discountPct(course);
  const byline = [course.instructor_name, course.category_name].filter(Boolean).join(' · ');

  return (
    <div className="relative h-full">
      <Link
        to={`/course/${course.slug}`}
        className="group flex h-full flex-col gap-3.5 rounded-2xl border border-lms-line bg-lms-surface p-4 hover:border-[var(--lms-hover-border)] hover:shadow-[var(--lms-elev-2)]"
      >
        <div className="relative">
          {course.thumbnail ? (
            <img src={course.thumbnail} alt="" className="aspect-video w-full rounded-[10px] object-cover" loading="lazy" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-[10px] bg-lms-sidebar p-5 text-center">
              <span className="line-clamp-3 text-lg font-bold leading-snug text-white">{course.title}</span>
            </div>
          )}
          {course.is_free ? (
            <span className="absolute left-2.5 top-2.5 rounded-md bg-lms-chip-bg px-2 py-0.5 text-xs font-bold text-lms-chip-free">
              {t('Free')}
            </span>
          ) : pct ? (
            <span className="absolute left-2.5 top-2.5 rounded-md bg-lms-sale px-2 py-0.5 text-xs font-bold text-white lms-num">
              -{pct}%
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <h3 className="lms-clamp2 text-[17px] font-bold leading-snug text-lms-ink group-hover:text-lms-link">
            {course.title}
          </h3>
          {course.short_description && (
            <p className="lms-clamp2 text-sm leading-relaxed text-lms-sub">{course.short_description}</p>
          )}
          {byline && <span className="text-[12.5px] text-lms-sub">{byline}</span>}
        </div>

        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="text-base"><Price course={course} /></span>
          <span className="flex items-center gap-3">
            <Rating rating={course.rating} />
            {course.level && <span className="text-[12.5px] capitalize text-lms-sub">{t(course.level)}</span>}
          </span>
        </div>
      </Link>
      {isLoggedIn() && <CardMenu courseId={course.id} />}
    </div>
  );
}
